"use client";

import { useTranslations } from "next-intl";
import { SectionShell, EditorialTitle, GradientText, AnimatedBar, RevealOnScroll } from "./_primitives";

export default function PayoutMechanicsSection() {
    const t = useTranslations();

    const cases = [
        {
            id: 1,
            tag: t("joinInstructor_payout_case1_tag"),
            initials: "SO",
            name: "Sophie",
            sub: t("joinInstructor_payout_case1_sub"),
            split: [{ label: t("joinInstructor_payout_label_you"), pct: 100 }],
            payout: "9,95",
            sentence: t("joinInstructor_payout_case1_sentence"),
        },
        {
            id: 2,
            tag: t("joinInstructor_payout_case2_tag"),
            initials: "MA",
            name: "Marc",
            sub: t("joinInstructor_payout_case2_sub"),
            split: [
                { label: t("joinInstructor_payout_label_you"), pct: 50 },
                { label: t("joinInstructor_payout_label_other"), pct: 50 },
            ],
            payout: "4,98",
            sentence: t("joinInstructor_payout_case2_sentence"),
        },
        {
            id: 3,
            tag: t("joinInstructor_payout_case3_tag"),
            initials: "LE",
            name: "Léa",
            sub: t("joinInstructor_payout_case3_sub"),
            split: [
                { label: t("joinInstructor_payout_label_you"), pct: 90 },
                { label: t("joinInstructor_payout_label_others"), pct: 10 },
            ],
            payout: "8,96",
            sentence: t("joinInstructor_payout_case3_sentence"),
        },
    ];

    return (
        <SectionShell label={t("joinInstructor_payout_marker")}>
            <div className="grid lg:grid-cols-3 gap-8 sm:gap-10 lg:gap-12 items-start mb-10 sm:mb-14">
                <RevealOnScroll className="lg:col-span-2">
                    <EditorialTitle className="text-4xl sm:text-5xl md:text-6xl">
                        {t("joinInstructor_payout_title_part1")}
                        <br />
                        {t("joinInstructor_payout_title_part2")}{" "}
                        <GradientText>{t("joinInstructor_payout_title_emphasis")}</GradientText>.
                    </EditorialTitle>
                </RevealOnScroll>
                <RevealOnScroll delay={0.1}>
                    <div className="rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-5 font-mono text-xs space-y-2">
                        <Row k={t("joinInstructor_payout_meta_fund")} v={t("joinInstructor_payout_meta_fund_value")} />
                        <Row k={t("joinInstructor_payout_meta_dist")} v={t("joinInstructor_payout_meta_dist_value")} />
                        <Row k={t("joinInstructor_payout_meta_trigger")} v={t("joinInstructor_payout_meta_trigger_value")} />
                    </div>
                </RevealOnScroll>
            </div>

            <div className="grid lg:grid-cols-3 gap-5">
                {cases.map((c, i) => (
                    <RevealOnScroll key={c.id} delay={i * 0.1}>
                        <div className="h-full rounded-2xl border border-gray-200 dark:border-white/10 bg-gray-50 dark:bg-white/[0.03] p-7 flex flex-col">
                            <div className="text-[10px] uppercase tracking-[0.25em] text-primary mb-4 font-mono">
                                {t("joinInstructor_payout_case_label")} {String(c.id).padStart(2, "0")} · {c.tag}
                            </div>
                            <h3
                                className="text-lg sm:text-xl md:text-2xl text-gray-900 dark:text-white font-bold mb-4 leading-tight"
                                style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                            >
                                {t("joinInstructor_payout_case_title", { name: c.name })}
                            </h3>

                            <div className="rounded-xl border border-gray-200 dark:border-white/10 bg-white dark:bg-[#0E161D] p-3 mb-6 flex items-center gap-3">
                                <span className="w-9 h-9 rounded-full bg-gradient-to-br from-[#FF7A1A] to-[#E51578] text-white text-xs font-bold flex items-center justify-center">
                                    {c.initials}
                                </span>
                                <div>
                                    <div className="text-sm text-gray-900 dark:text-white">
                                        {t("joinInstructor_payout_subscription_summary", {
                                            name: c.name,
                                        })}
                                    </div>
                                    <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400 dark:text-white/40 font-mono">
                                        {c.sub}
                                    </div>
                                </div>
                            </div>

                            <div className="space-y-3 mb-6">
                                {c.split.map((s, idx) => (
                                    <div key={idx}>
                                        <div className="flex justify-between text-[11px] uppercase tracking-[0.2em] text-gray-500 dark:text-white/55 mb-1.5 font-mono">
                                            <span>{s.label}</span>
                                            <span>{s.pct}%</span>
                                        </div>
                                        <AnimatedBar
                                            percent={s.pct}
                                            from={s.label === t("joinInstructor_payout_label_you")
                                                ? "from-[#FF7A1A]"
                                                : "from-gray-300 dark:from-white/30"}
                                            to={s.label === t("joinInstructor_payout_label_you")
                                                ? "to-[#E51578]"
                                                : "to-gray-300 dark:to-white/30"}
                                            delay={0.4 + idx * 0.15}
                                        />
                                    </div>
                                ))}
                            </div>

                            <div className="mt-auto pt-5 border-t border-dashed border-gray-200 dark:border-white/10">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-gray-400 dark:text-white/40 mb-1 font-mono">
                                    {t("joinInstructor_payout_you_get")}
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <span
                                        className="text-4xl font-bold text-primary"
                                        style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                                    >
                                        {c.payout} €
                                    </span>
                                    <span className="text-xs text-gray-400 dark:text-white/40 font-mono">
                                        {c.sentence}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </RevealOnScroll>
                ))}
            </div>

            <RevealOnScroll delay={0.4}>
                <p
                    className="mt-10 text-center italic text-gray-600 dark:text-white/70 max-w-3xl mx-auto"
                    style={{ fontFamily: "'Playfair Display', Georgia, serif" }}
                >
                    {t("joinInstructor_payout_bonus_part1")}{" "}
                    <span className="text-primary font-semibold not-italic">
                        {t("joinInstructor_payout_bonus_emphasis")}
                    </span>
                    {t("joinInstructor_payout_bonus_part2")}
                </p>
            </RevealOnScroll>
        </SectionShell>
    );
}

function Row({ k, v }) {
    return (
        <div className="flex justify-between text-gray-500 dark:text-white/55">
            <span>{k}</span>
            <span className="text-primary">{v}</span>
        </div>
    );
}
